import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from '../../../components/LogoIcon';

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const loginRef = useRef<HTMLDivElement>(null);

  const isEn = i18n.language === 'en';

  const navLinks = [
    { label: t('navbar.links.features'), href: '#features' },
    { label: t('navbar.links.methods'), href: '#methods' },
    { label: t('navbar.links.platform'), href: '#platform' },
    { label: t('navbar.links.integrations'), href: '#integrations' },
    { label: t('navbar.links.pricing'), href: '#pricing' },
    { label: t('navbar.links.testimonials'), href: '#testimonials' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (loginRef.current && !loginRef.current.contains(e.target as Node)) {
        setLoginOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileOpen(false);
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLanguageSwitch = () => {
    setMobileOpen(false);
    navigate(isEn ? '/' : '/en');
  };

  return (
    <nav className={`hp-nav${scrolled ? ' hp-nav--scrolled' : ''}`} aria-label={t('navbar.menuAriaLabel')}>
      <div className="hp-nav-inner">
        <a
          href="#hero"
          className="hp-nav-logo"
          onClick={(e) => handleSmoothScroll(e, '#hero')}
        >
          <Logo height={40} textColor="white" />
        </a>

        <div className="hp-nav-links">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleSmoothScroll(e, link.href)}
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hp-nav-cta">
          <button className="hp-btn-lang" onClick={handleLanguageSwitch}>
            {t('lang.switchTo')}
          </button>
          <div className="hp-login-dropdown" ref={loginRef}>
            <button
              className="hp-btn-ghost"
              onClick={() => setLoginOpen(!loginOpen)}
              aria-expanded={loginOpen}
            >
              {t('navbar.login')} &#9662;
            </button>
            {loginOpen && (
              <div className="hp-login-dropdown-menu">
                <Link to="/login" onClick={() => setLoginOpen(false)}>
                  <span className="hp-login-dropdown-icon">&#128188;</span>
                  <span>
                    <strong>{t('navbar.coachLogin')}</strong>
                    <small>{t('navbar.coachLoginSub')}</small>
                  </span>
                </Link>
                <Link to="/portal/login" onClick={() => setLoginOpen(false)}>
                  <span className="hp-login-dropdown-icon">&#128100;</span>
                  <span>
                    <strong>{t('navbar.clientPortal')}</strong>
                    <small>{t('navbar.clientPortalSub')}</small>
                  </span>
                </Link>
              </div>
            )}
          </div>
          <Link to="/register" className="hp-btn-primary">{t('navbar.cta')}</Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`hp-hamburger${mobileOpen ? ' hp-hamburger--open' : ''}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={t('navbar.hamburgerAriaLabel')}
          aria-expanded={mobileOpen}
        >
          <span /><span /><span />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="hp-mobile-menu">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleSmoothScroll(e, link.href)}
            >
              {link.label}
            </a>
          ))}
          <div className="hp-mobile-menu-cta">
            <button className="hp-btn-lang hp-btn-lang--mobile" onClick={handleLanguageSwitch}>
              {t('lang.switchTo')}
            </button>
            <Link to="/login" onClick={() => setMobileOpen(false)}>{t('navbar.coachLogin')}</Link>
            <Link to="/portal/login" onClick={() => setMobileOpen(false)}>{t('navbar.clientPortal')}</Link>
            <Link to="/register" className="hp-btn-primary" onClick={() => setMobileOpen(false)}>{t('navbar.cta')}</Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
